/*
  Recently viewed products carousel (sections/boomn-recently-viewed.liquid and
  sections/boomn-recently-viewed-collection.liquid).

  data-mode="static" skips steps 1-3 and keeps the server-rendered products.
  data-collection limits recently viewed cards to products in that collection.

  1. Reads product handles saved by snippets/recently-viewed-tracker.liquid.
  2. Fetches each card from sections/boomn-recently-viewed-card.liquid via the
     Section Rendering API. Sold out / excluded products come back empty and are skipped.
  3. Puts recently viewed cards first, fills the rest from the server-rendered
     fallback collection, then refreshes the slider and pagination.
*/
if (!customElements.get('recently-viewed-products')) {
  const RV_STORAGE_KEY = 'eb_recently_viewed';
  const RV_FETCH_LIMIT = 20;

  const readHandles = () => {
    try {
      const list = JSON.parse(localStorage.getItem(RV_STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
    } catch (e) {
      return [];
    }
  };

  const forgetHandles = (handles) => {
    if (!handles.length) return;
    try {
      const list = readHandles().filter((h) => !handles.includes(h));
      localStorage.setItem(RV_STORAGE_KEY, JSON.stringify(list));
    } catch (e) {}
  };

  class RecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.cardSection = this.dataset.cardSection;
      this.max = parseInt(this.dataset.max, 10) || 12;
      this.minRecent = parseInt(this.dataset.minRecent, 10) || 1;
      this.fill = this.dataset.fill === 'true';
      // Optional (boomn-recently-viewed-collection): only show recently viewed products in this collection.
      this.collectionHandle = this.dataset.collection || '';
      // 'static' shows the server-rendered products only and skips recently viewed.
      this.mode = this.dataset.mode || 'recent';
      // Products already in the cart; skipped when filling after recently viewed cards load.
      this.cartProductIds = new Set((this.dataset.cartProducts || '').split(',').filter(Boolean));

      this.list = this.querySelector('[data-rv-list]');
      this.sliderComponent = this.querySelector('slider-component');
      this.dots = this.querySelector('[data-rv-dots]');

      this.init();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    async init() {
      const handles = this.mode === 'static' ? [] : readHandles();
      if (handles.length) {
        try {
          await this.loadRecent(handles.slice(0, RV_FETCH_LIMIT));
        } catch (e) {
          console.warn('[recently-viewed]', e);
        }
      }
      this.classList.remove('rv--pending');

      await customElements.whenDefined('slider-component');
      this.refreshSlider();

      this.resizeObserver = new ResizeObserver(() => this.refreshSlider());
      this.resizeObserver.observe(this.list);
      this.list.addEventListener('scroll', () => this.updateDots(), { passive: true });
    }

    async loadRecent(handles) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const missing = [];
      // Through a collection URL, the card section checks membership and returns nothing for outside products.
      const base = this.collectionHandle ? `${root}collections/${encodeURIComponent(this.collectionHandle)}/` : root;

      const results = await Promise.all(
        handles.map((handle) =>
          fetch(`${base}products/${encodeURIComponent(handle)}?section_id=${this.cardSection}`)
            .then((res) => {
              // A 404 on a collection URL can mean "not in this collection", so only prune on plain product URLs.
              if (res.status === 404 && !this.collectionHandle) missing.push(handle);
              return res.ok ? res.text() : '';
            })
            .catch(() => '')
        )
      );
      forgetHandles(missing);

      const parser = new DOMParser();
      const seen = new Set();
      const recent = [];
      for (const html of results) {
        if (!html || recent.length >= this.max) continue;
        const doc = parser.parseFromString(html, 'text/html');
        const card = doc.querySelector('[data-rv-card]');
        if (!card || seen.has(card.dataset.productId)) continue;
        if (this.collectionHandle) {
          const marker = doc.querySelector('[data-rv-collection]');
          if (!marker || marker.dataset.rvCollection !== this.collectionHandle) continue;
        }
        seen.add(card.dataset.productId);
        // Cards come back with a shared 'rv-recent' ID prefix; make it unique per section.
        card.querySelectorAll('[id], [aria-labelledby]').forEach((el) => {
          ['id', 'aria-labelledby'].forEach((attr) => {
            const value = el.getAttribute(attr);
            if (value) el.setAttribute(attr, value.replaceAll('rv-recent', `rv-${this.sectionId}`));
          });
        });
        recent.push(document.importNode(card, true));
      }

      if (recent.length < this.minRecent) return;

      const slides = [...recent];
      if (this.fill) {
        this.list.querySelectorAll('[data-rv-card]').forEach((card) => {
          if (slides.length >= this.max || seen.has(card.dataset.productId)) return;
          if (this.cartProductIds.has(card.dataset.productId)) return;
          seen.add(card.dataset.productId);
          slides.push(card);
        });
      }

      slides.forEach((slide, i) => (slide.id = `Slide-${this.sectionId}-${i + 1}`));
      this.list.replaceChildren(...slides);
      this.list.scrollLeft = 0;

      this.querySelector('[data-rv-heading="fallback"]')?.setAttribute('hidden', '');
      this.querySelector('[data-rv-heading="recent"]')?.removeAttribute('hidden');

      // Judge.me badges arrive as server HTML; let the app restyle them if it is loaded.
      try {
        window.jdgm?.customizeBadges?.();
      } catch (e) {}
    }

    getMetrics() {
      const slides = Array.from(this.list.children).filter((el) => el.clientWidth > 0);
      if (slides.length < 2) return { slides, perPage: slides.length || 1, offset: 0, pages: 1 };
      const offset = slides[1].offsetLeft - slides[0].offsetLeft;
      // N slides fit in N * offset - gap, so add the gap back before dividing.
      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      const perPage = Math.max(1, Math.floor((this.list.clientWidth + gap + 1) / offset));
      const pages = Math.max(1, Math.ceil(slides.length / perPage));
      return { slides, perPage, offset, pages };
    }

    refreshSlider() {
      const { slides, perPage, pages } = this.getMetrics();
      this.classList.toggle('rv--single-page', pages <= 1);

      // Hide the whole section if there is nothing to show (no history and an empty fallback).
      this.closest('.shopify-section')?.toggleAttribute('hidden', this.list.children.length === 0);

      // Arrows move a full page, like the Rebuy widget.
      this.querySelectorAll('.rv__arrow').forEach((btn) => (btn.dataset.step = perPage));
      const slider = this.sliderComponent;
      if (slider && typeof slider.resetPages === 'function') {
        slider.resetPages();
        // Dawn's initPages undercounts slides per page by one when there is no peek; correct it for the progress bar.
        if (slides.length > 1) {
          slider.slidesPerPage = perPage;
          slider.totalPages = Math.max(1, slides.length - perPage + 1);
          slider.update();
        }
      }
      this.buildDots(pages);
    }

    buildDots(pages) {
      if (!this.dots) return;
      if (this.dots.childElementCount !== pages) {
        const buttons = Array.from({ length: pages }, (_, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'rv__dot';
          btn.setAttribute('aria-label', `Go to page ${i + 1} of ${pages}`);
          btn.addEventListener('click', () => this.goToPage(i));
          return btn;
        });
        this.dots.replaceChildren(...buttons);
      }
      this.updateDots();
    }

    goToPage(index) {
      const { perPage, offset } = this.getMetrics();
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      this.list.scrollTo({ left: Math.min(index * perPage * offset, maxScroll), behavior: 'smooth' });
    }

    updateDots() {
      if (!this.dots || !this.dots.childElementCount) return;
      const { perPage, offset, pages } = this.getMetrics();
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      let active = offset ? Math.round(this.list.scrollLeft / (perPage * offset)) : 0;
      if (this.list.scrollLeft >= maxScroll - 2) active = pages - 1;
      active = Math.min(Math.max(active, 0), pages - 1);
      Array.from(this.dots.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === active);
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
